import React from 'react';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import moment from 'moment';
import { isDefined } from '../../helpers/utils';
import {
  isInstructionHubBlockedAssignment,
  isSelectableAssignment
} from '../../helpers/instruction-hub/instruction-hub-assignments';

// Get students from list of classrooms
export const getStudents = (classrooms) => {
  if (classrooms.length > 0) {
    const classroom = classrooms[0];
    const group = classroom.groups[0];
    return group.students;
  }
  return [];
};

export const getTopicsAndStandards = (genericCourses) => {
  if (!isDefined(genericCourses) || genericCourses.length === 0) return [];

  const standardTopic = [];
  const topicTree = genericCourses.topics
    .map((topic) => {
      if (topic.type.toLowerCase() === 'standard') {
        standardTopic.push({
          ...topic,
          displayName: `${topic.externalKey.toUpperCase()} ${
            topic.name
          }`.trim(),
          selected: true
        });
      } else if (topic.type.toLowerCase() === 'strand') {
        return {
          ...topic,
          displayName: topic.name,
          selected: true,
          childTopics: topic.childTopics.map((st) => ({
            ...st,
            displayName: `${st.externalKey.toUpperCase()} ${st.name}`.trim(),
            selected: true
          })),
          isExpand: !!topic.childTopics.find((s) => s.selected)
        };
      }
      return undefined;
    })
    .filter((topic) => isDefined(topic));

  if (standardTopic.length > 0) {
    topicTree.push({
      id: -1,
      displayName: 'All Standards',
      childTopics: standardTopic,
      selected: true
    });
  }

  if (standardTopic.length === 0 && topicTree.length > 0) {
    topicTree[0].selected = true;
    topicTree[0].childTopics = topicTree[0].childTopics?.map((ct) => ({
      ...ct,
      selected: true
    }));
    topicTree[0].isExpand = topicTree[0].childTopics?.length > 0;
  }
  return topicTree;
};

export const getFilteredTopics = (searchTerm, topics) => {
  return topics.map((topic) => ({
    ...topic,
    isHidden: searchTerm
      ? !(topic.topicName.toLowerCase().indexOf(searchTerm.toLowerCase()) >= 0)
      : false
  }));
};

export const updateStrandAndStandards = (id, checked, topics) => {
  if (!id) {
    return topics;
  }
  return topics.map((val) => ({
    ...val,
    selected:
      val.topicId === parseInt(id, 10) ? checked === 'checked' : val?.selected
  }));
};

export const updateActivitySelection = (
  id,
  topics,
  checkedNormal,
  checkedEasier
) => {
  if (!id) {
    return topics;
  }
  return topics.map((topic) => {
    if (topic.topicId === parseInt(id, 10)) {
      return {
        ...topic,
        checkedNormal,
        checkedEasier
      };
    }
    return topic;
  });
};

export const updateStrands = (checked, topics) => {
  return topics.map((topic) => ({
    ...topic,
    selected: checked,
    childTopics: topic.childTopics.map((ct) => ({
      ...ct,
      selected: checked
    })),
    isExpand: topic.childTopics.length > 0 && checked
  }));
};

export const updateCheckedStatus = (checked, topics) => {
  return topics.map((topic) => ({
    ...topic,
    selected: checked
  }));
};

const hasScoreInRange = (assignment, isInRange) =>
  isDefined(assignment.result?.score) && isInRange(assignment.result.score);

const QUICK_SELECTION_MATCHERS = {
  allCells: () => true,
  allAssigned: (assignment) => isDefined(assignment.assignment),
  result50: (assignment) => hasScoreInRange(assignment, (score) => score <= 50),
  result85: (assignment) =>
    hasScoreInRange(assignment, (score) => score > 50 && score < 85),
  result100: (assignment) => hasScoreInRange(assignment, (score) => score >= 85)
};

export const applyQuickSelection = (
  studentAssignments,
  selectionType,
  blockedLookup
) => {
  if (selectionType === 'clearSelections') {
    return studentAssignments.map((assignment) =>
      assignment.isHidden
        ? assignment
        : { ...assignment, isSelected: undefined }
    );
  }

  const isMatch = QUICK_SELECTION_MATCHERS[selectionType];
  if (!isMatch) {
    return null;
  }

  return studentAssignments.map((assignment) =>
    isSelectableAssignment(assignment, blockedLookup) && isMatch(assignment)
      ? { ...assignment, isSelected: true }
      : assignment
  );
};

export const updateGridCells = (
  indexes,
  assignments,
  status,
  blockedLookup
) => {
  indexes.forEach((index) => {
    if (
      isSelectableAssignment(
        assignments.studentAssignments[index],
        blockedLookup
      )
    ) {
      assignments.studentAssignments[index] = {
        ...assignments.studentAssignments[index],
        isSelected: status === 'checked'
      };
    }
  });
  return assignments?.studentAssignments || [];
};

export const getSelectedStandardCount = (topics) => {
  let selectedTopicCount = 0;
  topics.forEach((topic) => {
    if (topic.selected) {
      selectedTopicCount += 1;
    }
  });
  return selectedTopicCount;
};

export const updateSelectedTopics = (updateTopics) => {
  if (!updateTopics) {
    return { updateTopics, selectedTopicCount: 0 };
  }
  let selectedTopicCount = 0;
  const newTopics = updateTopics.map((topic) => {
    if (topic.selected) {
      selectedTopicCount += 1;
    }
    return {
      ...topic,
      selected: true,
      checkedNormal: true,
      checkedEasier: false,
      isHidden: false
    };
  });

  return { updateTopics: newTopics, selectedTopicCount };
};

export const updateHiddenTopics = (filteredTopics, updateTopics) => {
  if (!isDefined(updateTopics)) {
    return { updateTopics, selectedTopicCount: 0 };
  }
  let selectedTopicCount = 0;
  const newUpdateTopics = updateTopics.map((topic) => {
    const filterTopic = filteredTopics.find(
      (selTopic) => selTopic.topicId === topic.topicId
    );
    if (filterTopic) {
      if (filterTopic.selected) {
        selectedTopicCount += 1;
      }
      return {
        ...topic,
        selected: filterTopic.selected,
        checkedNormal: filterTopic.checkedNormal,
        checkedEasier: filterTopic.checkedEasier,
        isHidden: filterTopic.isHidden
      };
    }
    selectedTopicCount += 1;
    return {
      ...topic,
      selected: true,
      checkedNormal: true,
      checkedEasier: false,
      isHidden: false
    };
  });

  return { updateTopics: newUpdateTopics, selectedTopicCount };
};

export const updateAssignmentStatusByTopic = (
  activities,
  studentId,
  studentAssignments,
  blockedLookup
) => {
  if (studentAssignments?.length > 0 && activities?.length > 0) {
    return studentAssignments.map((s) => {
      const findActivity = activities.find(
        (activity) =>
          s.encryptedUserProfileId === studentId &&
          s.activityId === activity.activityId &&
          s.taskType === activity.taskType
      );
      let isSelected = s.isSelected;
      if (
        isDefined(findActivity) &&
        !isInstructionHubBlockedAssignment(s, blockedLookup)
      ) {
        isSelected = s.isSelected ? undefined : true;
      }
      return {
        ...s,
        isSelected
      };
    });
  }

  return studentAssignments;
};

export function checkIfAssignmentPassesFilter(studentAssignment, payload) {
  let passesFilter = true;
  // Score can very rarely be null, which counts as non-attempted, even though there is attempts...
  const hasAttempt =
    studentAssignment?.result?.completionDate &&
    studentAssignment?.result.attempts > 0 &&
    isDefined(studentAssignment?.result.score);
  const hasAssignment = studentAssignment?.assignment?.startDate;

  if (isDefined(payload.dateFilter)) {
    if (!hasAttempt && !hasAssignment) {
      return { ...studentAssignment, isFiltered: true };
    }
    const { startDate, endDate } = payload.dateFilter.dateRange;

    const isInRange = (date) =>
      isDefined(date) &&
      moment(date).isBetween(startDate, endDate, undefined, '[)');

    const assignmentPassesFilter = isInRange(
      studentAssignment?.assignment?.startDate
    );

    const resultPassesFilter = isInRange(
      studentAssignment?.result?.completionDate
    );

    passesFilter = assignmentPassesFilter || resultPassesFilter;
  }
  let passesRange = true;
  if (isDefined(studentAssignment?.result?.score)) {
    if (studentAssignment?.result?.score <= 50) {
      passesRange = payload.rangeData.zeroToFifty;
    } else if (
      studentAssignment?.result?.score > 50 &&
      studentAssignment?.result?.score < 85
    ) {
      passesRange = payload.rangeData.fiftyToEightyFive;
    } else if (studentAssignment?.result?.score >= 85) {
      passesRange = payload.rangeData.overEightyFive;
    }
  }

  let passesDropDownValue = true;
  if (payload.dropdownValue.selectedOption === 'AO') {
    passesDropDownValue =
      studentAssignment?.result?.isAssignedResult &&
      studentAssignment?.result?.score !== null;
  }

  if (payload.rangeData?.assigned === false) {
    passesFilter = passesFilter && !isDefined(studentAssignment.assignment);
  }
  passesFilter = passesFilter && passesRange && passesDropDownValue;
  return { ...studentAssignment, isFiltered: !passesFilter };
}

const contactTechSupport = (
  <a href="http://www.3plearning.com/contact/">
    {i18n._(t('help.contactSupport')`Please contact Tech Support`)}
  </a>
);
const errorDetails = i18n._(
  t(
    'help.errorDetails'
  )`with details of what happened and we will work to get you back on track.`
);
export const contactTechSupportMessage = `${i18n._(
  t('help.errorOccurred')`Oops an error has occurred. `
)}
${contactTechSupport}
${errorDetails}`;

export function sortStudentAssignments(
  studentAssignments,
  sortedStudents,
  selectedActivities
) {
  const orderedArr = [];
  sortedStudents.forEach((sorted) => {
    studentAssignments.forEach((currentStudent) => {
      let isHidden = false;
      if (
        !selectedActivities.some(
          (selectedActivity) =>
            selectedActivity?.length > 0 &&
            selectedActivity.find(
              (x) =>
                x.activityId === currentStudent.activityId &&
                x.taskType === currentStudent.taskType
            )
        )
      ) {
        isHidden = true;
      } else {
        isHidden = false;
      }
      if (
        currentStudent.encryptedUserProfileId === sorted.encryptedUserProfileId
      ) {
        orderedArr.push({ ...currentStudent, student: sorted, isHidden });
      }
    });
  });
  return orderedArr;
}

export function sortStudentAssignmentsByName(
  studentAssignments,
  sortedStudents
) {
  const orderedArr = [];
  sortedStudents.forEach((sorted) => {
    studentAssignments.forEach((currentStudent) => {
      if (
        currentStudent.encryptedUserProfileId === sorted.encryptedUserProfileId
      ) {
        orderedArr.push({ ...currentStudent, student: sorted });
      }
    });
  });
  return orderedArr;
}

export function updateStudentAssignmentsHidden(
  studentAssignments,
  selectedActivities
) {
  const orderedArr = studentAssignments.map((currentStudent) => {
    if (
      !selectedActivities.some(
        (selectedActivity) =>
          selectedActivity?.length > 0 &&
          selectedActivity.find(
            (x) =>
              x.activityId === currentStudent.activityId &&
              x.taskType === currentStudent.taskType
          )
      )
    ) {
      return { ...currentStudent, isHidden: true };
    }

    return { ...currentStudent, isHidden: false };
  });
  return orderedArr;
}



// WEBPACK FOOTER //
// ./containers/course-assign/course-assign-util.js